import {
    Pause,
    Phone,
    PhoneCall,
    PhoneForwarded,
    PhoneIncoming,
    PhoneMissed,
    PhoneOff,
    PhoneOutgoing,
    Voicemail,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { CallDirection, CallStatus } from '../types';
import { serverNow } from './clock-offset';

/** Raw status refined with direction/answer context — what a call LOOKS like. */
export type CallVisualState =
    | 'COMPLETED_INBOUND'
    | 'COMPLETED_OUTBOUND'
    | 'MISSED'
    | 'VOICEMAIL'
    | 'FAILED'
    | 'BUSY'
    | 'NO_ANSWER'
    | 'CANCELED'
    | 'IN_PROGRESS'
    | 'RINGING'
    | 'INITIATED'
    | 'ON_HOLD'
    | 'TRANSFERRED';

export interface ICallStateMeta {
    readonly label: string;
    readonly icon: LucideIcon;
    readonly badgeVariant: 'default' | 'success' | 'warning' | 'danger' | 'info';
    /** Tinted-circle classes for list/drawer icon chips. */
    readonly chipClass: string;
    /** CSS color for BadgeDot / widget dot usage. */
    readonly dotColor: string;
    /** Live states render an animate-ping halo. */
    readonly pulse?: boolean;
}

/**
 * There is no MISSED status in the enum — missed = INBOUND call that was
 * never answered (`answeredAt === null`, status COMPLETED or NO_ANSWER).
 */
export function resolveCallVisualState(call: {
    readonly status: CallStatus;
    readonly direction: CallDirection;
    readonly answeredAt: string | null;
}): CallVisualState {
    if (call.status === 'COMPLETED') {
        if (call.direction === 'INBOUND') {
            return call.answeredAt === null ? 'MISSED' : 'COMPLETED_INBOUND';
        }
        return 'COMPLETED_OUTBOUND';
    }
    if (call.status === 'NO_ANSWER') {
        return call.direction === 'INBOUND' ? 'MISSED' : 'NO_ANSWER';
    }
    return STATUS_TO_VISUAL_STATE[call.status];
}

/** Single source of truth for how each call state looks (B4 legend). */
export const CALL_STATE_META: Record<CallVisualState, ICallStateMeta> = {
    COMPLETED_INBOUND: {
        label: 'Completed',
        icon: PhoneIncoming,
        badgeVariant: 'success',
        chipClass: 'bg-success-500/10 text-success-500',
        dotColor: 'var(--color-success-500)',
    },
    COMPLETED_OUTBOUND: {
        label: 'Completed',
        icon: PhoneOutgoing,
        badgeVariant: 'info',
        chipClass: 'bg-info-500/10 text-info-500',
        dotColor: 'var(--color-info-500)',
    },
    MISSED: {
        label: 'Missed',
        icon: PhoneMissed,
        badgeVariant: 'danger',
        chipClass: 'bg-error-500/10 text-error-500',
        dotColor: 'var(--color-error-500)',
    },
    VOICEMAIL: {
        label: 'Voicemail',
        icon: Voicemail,
        badgeVariant: 'warning',
        chipClass: 'bg-warning-500/10 text-warning-500',
        dotColor: 'var(--color-warning-500)',
    },
    FAILED: {
        label: 'Failed',
        icon: PhoneOff,
        badgeVariant: 'danger',
        chipClass: 'bg-error-500/10 text-error-500',
        dotColor: 'var(--color-error-500)',
    },
    BUSY: {
        label: 'Busy',
        icon: PhoneOff,
        badgeVariant: 'danger',
        chipClass: 'bg-error-500/10 text-error-500',
        dotColor: 'var(--color-error-500)',
    },
    NO_ANSWER: {
        label: 'No answer',
        icon: PhoneMissed,
        badgeVariant: 'default',
        chipClass: 'bg-bg-icon-tile text-text-tertiary',
        dotColor: 'var(--color-neutral-400)',
    },
    CANCELED: {
        label: 'Canceled',
        icon: PhoneOff,
        badgeVariant: 'default',
        chipClass: 'bg-bg-icon-tile text-text-tertiary',
        dotColor: 'var(--color-neutral-400)',
    },
    IN_PROGRESS: {
        label: 'In progress',
        icon: PhoneCall,
        badgeVariant: 'success',
        chipClass: 'bg-success-500/10 text-success-500',
        dotColor: 'var(--color-success-500)',
        pulse: true,
    },
    RINGING: {
        label: 'Ringing…',
        icon: Phone,
        badgeVariant: 'warning',
        chipClass: 'bg-warning-500/10 text-warning-500',
        dotColor: 'var(--color-warning-500)',
        pulse: true,
    },
    INITIATED: {
        label: 'Connecting…',
        icon: Phone,
        badgeVariant: 'warning',
        chipClass: 'bg-warning-500/10 text-warning-500',
        dotColor: 'var(--color-warning-500)',
        pulse: true,
    },
    ON_HOLD: {
        label: 'On hold',
        icon: Pause,
        badgeVariant: 'warning',
        chipClass: 'bg-warning-500/10 text-warning-500',
        dotColor: 'var(--color-warning-500)',
    },
    TRANSFERRED: {
        label: 'Transferring…',
        icon: PhoneForwarded,
        badgeVariant: 'info',
        chipClass: 'bg-info-500/10 text-info-500',
        dotColor: 'var(--color-info-500)',
    },
};

const STATUS_TO_VISUAL_STATE: Record<CallStatus, CallVisualState> = {
    INITIATED: 'INITIATED',
    RINGING: 'RINGING',
    IN_PROGRESS: 'IN_PROGRESS',
    ON_HOLD: 'ON_HOLD',
    PARKED: 'ON_HOLD',
    TRANSFERRED: 'TRANSFERRED',
    COMPLETED: 'COMPLETED_OUTBOUND',
    FAILED: 'FAILED',
    BUSY: 'BUSY',
    NO_ANSWER: 'NO_ANSWER',
    CANCELED: 'CANCELED',
    VOICEMAIL: 'VOICEMAIL',
};

/**
 * Status-only lookup for surfaces without direction/answer context (the call
 * widget). Table/drawer must use resolveCallVisualState instead.
 */
export function callStatusMeta(status: CallStatus): ICallStateMeta {
    return CALL_STATE_META[STATUS_TO_VISUAL_STATE[status]];
}

/** `125` -> `"02:05"`. */
export function formatCallDuration(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Elapsed seconds between an ISO start time and now — clamped to >= 0.
 * Measured on the SERVER's clock (`serverNow`), because the timestamps this is
 * called with are server-stamped; using the raw browser clock leaked machine
 * skew into the displayed duration.
 */
export function secondsSince(iso: string): number {
    const elapsed = Math.floor((serverNow() - new Date(iso).getTime()) / 1000);
    return Math.max(0, elapsed);
}
